import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ArrowDown, ArrowUp, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
    CircleAlert, Download, FileJson, FileSpreadsheet, FileText, Loader2,
    Printer, RefreshCw, Search, SlidersHorizontal,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import styles from './GstReport.module.css';
import { useStore } from '../../../../context/StoreContext';
import { useAuth } from '../../../../context/AuthContext';
import { SMIIFY_API } from '../../../../config/api';

// Backend address comes from src/config/api.js (never hard-coded)
const GST_REPORT_API = `${SMIIFY_API}/analytics/gst-report`;

const DEFAULT_COLUMNS = ['date', 'invoice', 'grossBilled', 'prepaidAdjusted', 'taxable', 'cgst', 'sgst', 'totalGst', 'grandTotal'];

// "Advance Adjusted" = prepaid card + khata + client package services (GST was charged when those were sold)
const COLUMN_DEFINITIONS = [
    { key: 'date', label: 'Invoice date', sortable: true },
    { key: 'invoice', label: 'Invoice no.', sortable: true },
    { key: 'customer', label: 'Customer details', sortable: true },
    { key: 'type', label: 'Type', sortable: true, align: 'center' },
    { key: 'grossBilled', label: 'Gross Bill', sortable: true, align: 'right' },
    { key: 'prepaidAdjusted', label: 'Advance Adjusted', sortable: true, align: 'right' },
    { key: 'packageAdjusted', label: 'Package services', sortable: true, align: 'right' },
    { key: 'taxable', label: 'Net Taxable', sortable: true, align: 'right' },
    { key: 'cgst', label: 'Net CGST', sortable: true, align: 'right' },
    { key: 'sgst', label: 'Net SGST', sortable: true, align: 'right' },
    { key: 'totalGst', label: 'Total Net GST', sortable: true, align: 'right' },
    { key: 'grandTotal', label: 'Net Payable', sortable: true, align: 'right' },
    { key: 'gstin', label: 'Customer GSTIN', sortable: true },
    { key: 'placeOfSupply', label: 'Place of supply', sortable: true },
    { key: 'status', label: 'Status', sortable: true, align: 'center' },
];

const MONEY_COLS = ['grossBilled', 'prepaidAdjusted', 'packageAdjusted', 'taxable', 'cgst', 'sgst', 'totalGst', 'grandTotal'];

const toNumber = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (value === null || value === undefined || value === '') return 0;
    const parsed = Number(String(value).replace(/[₹,\s%]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

const toText = (value, fallback = '') => (value === null || value === undefined || value === '') ? fallback : String(value);
const lower = (value) => toText(value).trim().toLowerCase();

const formatMoney = (value) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(toNumber(value));
const formatPdfMoney = (value) => toNumber(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const dateSortValue = (value) => Number.isNaN(new Date(value || 0).getTime()) ? 0 : new Date(value || 0).getTime();

const getStoreDetails = (store) => {
    if (!store) return { name: '', address: '', phone: '', gstin: '' };
    const name = toText(store?.name ?? store?.storeName ?? store?.businessName, 'Store');
    const addressParts = [
        toText(store?.address ?? store?.addressLine1 ?? store?.street ?? store?.shopAddress),
        toText(store?.addressLine2), toText(store?.area ?? store?.locality),
        toText(store?.city), toText(store?.state), toText(store?.pincode ?? store?.zip ?? store?.postalCode),
    ].filter(Boolean);
    return { name, address: addressParts.join(', '), phone: toText(store?.phone ?? store?.contactNumber ?? store?.mobile), gstin: toText(store?.gstin ?? store?.GSTIN ?? store?.gstNumber) };
};

/** Value of a money column on a row ("taxable" is stored as taxableAmount) */
const moneyOf = (row, key) => (key === 'taxable' ? row.taxableAmount : row[key]);

const normalizeRow = (row, index) => ({
    _key: `${row.invoiceId || row.id}-${row.rawDate || index}-${index}`,
    rawDate: row.rawDate || '',
    date: row.date || '—',
    invoice: row.id || '—',
    customer: { name: row.customer?.name || 'Walk-in customer', phone: row.customer?.phone || '—' },
    isB2B: Boolean(row.isB2B),
    type: row.isB2B ? 'B2B' : 'B2C',
    grossBilled: toNumber(row.grossBilled),
    prepaidAdjusted: toNumber(row.prepaidAdjusted),
    packageAdjusted: toNumber(row.packageAdjusted),
    taxableAmount: toNumber(row.taxableAmount),
    cgst: toNumber(row.cgst),
    sgst: toNumber(row.sgst),
    totalGst: toNumber(row.totalGst),
    grandTotal: toNumber(row.grandTotal),
    gstin: row.customer?.gstin || '—',
    placeOfSupply: row.placeOfSupply || '—',
    status: row.status || 'Recorded',
});

export default function GstReport({ dateFilter, onDataLoaded, onTotalsCalculated }) {
    const { activeStore } = useStore();
    const { user, hasPermission } = useAuth();

    const storeId = activeStore?._id || activeStore?.id || (typeof localStorage !== 'undefined' ? localStorage.getItem('activeStoreId') : '');
    const storeDetails = useMemo(() => getStoreDetails(activeStore), [activeStore]);

    const [gstData, setGstData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [query, setQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [sort, setSort] = useState({ key: 'date', direction: 'desc' });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [showColumns, setShowColumns] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);
    const [showExport, setShowExport] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [draggedColumn, setDraggedColumn] = useState(null);

    const columnControlRef = useRef(null);
    const exportControlRef = useRef(null);

    const canExport = hasPermission('reports', 'export');
    const exportConstraints = user?.policies?.reports?.constraints || {};
    const canExcel = exportConstraints.allowExcelExport !== false;
    const canPdf = exportConstraints.allowPdfExport !== false;
    const canCsv = exportConstraints.allowCsvExport !== false;
    const canPrint = exportConstraints.allowPrint !== false;
    const hasAnyExportOption = canExcel || canPdf || canCsv || canPrint;

    const fetchReport = useCallback(async (signal) => {
        if (!storeId || storeId === 'null' || storeId === 'undefined' || storeId === 'global_hq') {
            setGstData([]); setError('Select a branch to see its GST report.'); setIsLoading(false); return;
        }
        if (!dateFilter?.startDate || !dateFilter?.endDate) return;

        setIsLoading(true); setError('');
        try {
            const token = typeof localStorage !== 'undefined' ? localStorage.getItem('smiify_token') : '';
            const params = new URLSearchParams({
                storeId: String(storeId),
                period: dateFilter.value || 'thisMonth',
                startDate: dateFilter.startDate,
                endDate: dateFilter.endDate,
            });

            const response = await fetch(`${GST_REPORT_API}?${params.toString()}`, {
                headers: token ? { Authorization: `Bearer ${token}`, 'x-store-id': String(storeId) } : {}, signal,
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || result?.success === false) throw new Error(result?.message || 'Unable to load GST report.');

            const payload = result?.data?.rows ?? result?.data?.invoices ?? result?.data?.data ?? result?.data ?? [];
            setGstData(Array.isArray(payload) ? payload.map(normalizeRow) : []);
        } catch (requestError) {
            if (requestError?.name === 'AbortError') return;
            setError(requestError?.message === 'Failed to fetch' ? 'Cannot reach the server.' : (requestError?.message || 'Unable to load GST report.'));
        } finally {
            if (!signal.aborted) setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateFilter, refreshKey, storeId]);

    useEffect(() => {
        const controller = new AbortController();
        fetchReport(controller.signal);
        return () => controller.abort();
    }, [fetchReport]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (columnControlRef.current && !columnControlRef.current.contains(event.target)) setShowColumns(false);
            if (exportControlRef.current && !exportControlRef.current.contains(event.target)) setShowExport(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    useEffect(() => { setPage(1); }, [query, typeFilter, pageSize, dateFilter]);

    const filteredRows = useMemo(() => {
        const search = lower(query);
        const matching = gstData.filter((row) => {
            const haystack = [row.invoice, row.date, row.customer.name, row.customer.phone, row.gstin, row.type, row.placeOfSupply, row.status].join(' ').toLowerCase();
            return (!search || haystack.includes(search)) && (typeFilter === 'all' || row.type === typeFilter);
        });

        const getSortValue = (row) => {
            if (sort.key === 'date') return dateSortValue(row.rawDate);
            if (sort.key === 'invoice') return lower(row.invoice);
            if (sort.key === 'customer') return lower(row.customer.name);
            if (sort.key === 'type') return row.type;
            if (MONEY_COLS.includes(sort.key)) return moneyOf(row, sort.key);
            if (sort.key === 'gstin') return lower(row.gstin);
            if (sort.key === 'placeOfSupply') return lower(row.placeOfSupply);
            if (sort.key === 'status') return lower(row.status);
            return '';
        };

        return [...matching].sort((leftRow, rightRow) => {
            const left = getSortValue(leftRow); const right = getSortValue(rightRow);
            if (left === right) return 0;
            return (left > right ? 1 : -1) * (sort.direction === 'asc' ? 1 : -1);
        });
    }, [gstData, query, sort, typeFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    const visibleRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);
    const firstVisibleRow = filteredRows.length ? (page - 1) * pageSize + 1 : 0;
    const lastVisibleRow = Math.min(page * pageSize, filteredRows.length);

    useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

    const totals = useMemo(() => {
        const result = { totalGross: 0, totalPrepaid: 0, totalPackage: 0, totalTaxable: 0, totalCgst: 0, totalSgst: 0, totalGst: 0, grandTotal: 0, totalInvoices: filteredRows.length };
        filteredRows.forEach((row) => {
            result.totalGross += row.grossBilled;
            result.totalPrepaid += row.prepaidAdjusted;
            result.totalPackage += row.packageAdjusted;
            result.totalTaxable += row.taxableAmount;
            result.totalCgst += row.cgst;
            result.totalSgst += row.sgst;
            result.totalGst += row.totalGst;
            result.grandTotal += row.grandTotal;
        });
        return result;
    }, [filteredRows]);

    useEffect(() => { if (onDataLoaded) onDataLoaded(filteredRows); }, [filteredRows, onDataLoaded]);
    useEffect(() => { if (onTotalsCalculated) onTotalsCalculated(totals); }, [onTotalsCalculated, totals]);

    const orderedColumnDefinitions = visibleColumns.map((key) => COLUMN_DEFINITIONS.find((c) => c.key === key)).filter(Boolean);

    const cellText = (row, key) => {
        if (key === 'date') return row.date;
        if (key === 'invoice') return row.invoice;
        if (key === 'customer') return `${row.customer.name} - ${row.customer.phone}`;
        if (key === 'type') return row.type;
        if (key === 'gstin') return row.gstin;
        if (key === 'placeOfSupply') return row.placeOfSupply;
        if (key === 'status') return row.status;
        return '';
    };

    const getDynamicExportData = () => filteredRows.map((row) => {
        const exportObj = {};
        orderedColumnDefinitions.forEach((col) => {
            exportObj[col.label] = MONEY_COLS.includes(col.key) ? moneyOf(row, col.key) : cellText(row, col.key);
        });
        return exportObj;
    });

    const handleExcelExport = () => {
        if (!filteredRows.length) return;
        setIsExporting(true); setShowExport(false);
        setTimeout(() => {
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(getDynamicExportData()), 'GST Report');
            XLSX.writeFile(wb, `GST_Report_${dateFilter.value}_${Date.now()}.xlsx`);
            setIsExporting(false);
        }, 300);
    };

    const handleCSVExport = () => {
        if (!filteredRows.length) return;
        setIsExporting(true); setShowExport(false);
        setTimeout(() => {
            const csv = XLSX.utils.sheet_to_csv(XLSX.utils.json_to_sheet(getDynamicExportData()));
            const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `GST_Report_${dateFilter.value}_${Date.now()}.csv`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(link.href), 4000);
            setIsExporting(false);
        }, 300);
    };

    const handlePDFExport = () => {
        if (!filteredRows.length) return;
        setIsExporting(true); setShowExport(false);

        setTimeout(() => {
            const doc = new jsPDF('landscape');
            const pageWidth = doc.internal.pageSize.getWidth();
            const brandPrimary = [103, 61, 230];
            const textDark = [15, 23, 42];
            const generated = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }).format(new Date());

            doc.setFillColor(...brandPrimary);
            doc.rect(0, 0, pageWidth, 32, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.text(storeDetails.name || 'GST Output Report', 14, 14);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text(storeDetails.address || 'Address not provided', 14, 21);
            const contactString = [storeDetails.phone && `Ph: ${storeDetails.phone}`, storeDetails.gstin && `GSTIN: ${storeDetails.gstin}`].filter(Boolean).join('  |  ');
            if (contactString) doc.text(contactString, 14, 27);

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('GST SALES REPORT', pageWidth - 14, 14, { align: 'right' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text(`Period: ${dateFilter.label || 'Custom'}`, pageWidth - 14, 21, { align: 'right' });
            doc.text(`Generated: ${generated}`, pageWidth - 14, 27, { align: 'right' });

            const cursorY = 42;
            doc.setTextColor(...textDark);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('Tax Summary', 14, cursorY);

            autoTable(doc, {
                startY: cursorY + 4,
                head: [['Gross Billed (Rs)', 'Advance Adjusted (Rs)', 'of which Packages (Rs)', 'Net Taxable (Rs)', 'Net CGST (Rs)', 'Net SGST (Rs)', 'Total GST Payable (Rs)']],
                body: [[formatPdfMoney(totals.totalGross), formatPdfMoney(totals.totalPrepaid), formatPdfMoney(totals.totalPackage), formatPdfMoney(totals.totalTaxable), formatPdfMoney(totals.totalCgst), formatPdfMoney(totals.totalSgst), formatPdfMoney(totals.totalGst)]],
                theme: 'plain',
                headStyles: { fillColor: [248, 249, 250], textColor: [100, 116, 139], fontSize: 8, fontStyle: 'bold', halign: 'center' },
                bodyStyles: { textColor: textDark, fontSize: 10, fontStyle: 'bold', halign: 'center', cellPadding: 4 },
                margin: { left: 14, right: 14 },
            });

            const tableStartY = doc.lastAutoTable.finalY + 12;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('Invoice Details', 14, tableStartY);

            const head = [orderedColumnDefinitions.map((col) => col.label + (MONEY_COLS.includes(col.key) ? ' (Rs)' : ''))];
            const body = filteredRows.map((row) => orderedColumnDefinitions.map((col) => {
                if (MONEY_COLS.includes(col.key)) return formatPdfMoney(moneyOf(row, col.key));
                if (col.key === 'customer') return `${row.customer.name} ${row.customer.phone !== '—' ? `(${row.customer.phone})` : ''}`;
                return cellText(row, col.key);
            }));

            const columnStyles = {};
            orderedColumnDefinitions.forEach((col, idx) => {
                if (col.align === 'right') columnStyles[idx] = { halign: 'right' };
                if (col.align === 'center') columnStyles[idx] = { halign: 'center' };
                if (col.key === 'grandTotal' || col.key === 'totalGst') columnStyles[idx] = { ...columnStyles[idx], fontStyle: 'bold', textColor: col.key === 'grandTotal' ? [20, 137, 103] : textDark };
                if (col.key === 'prepaidAdjusted' || col.key === 'packageAdjusted') columnStyles[idx] = { ...columnStyles[idx], textColor: [180, 83, 9] };
            });

            autoTable(doc, {
                head, body, startY: tableStartY + 4, margin: { left: 10, right: 10 }, theme: 'grid',
                styles: { fontSize: 7.5, cellPadding: 3, textColor: textDark, lineColor: [226, 232, 240], lineWidth: 0.1 },
                headStyles: { fillColor: brandPrimary, textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [252, 253, 255] }, columnStyles,
                didDrawPage: () => {
                    doc.setFontSize(8); doc.setTextColor(100, 116, 139);
                    doc.text(`Page ${doc.getNumberOfPages()}`, pageWidth - 20, doc.internal.pageSize.getHeight() - 8);
                },
            });

            doc.save(`GST_Report_${Date.now()}.pdf`);
            setIsExporting(false);
        }, 300);
    };

    const setSortKey = (key) => setSort((c) => (c.key === key ? { key, direction: c.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }));
    const toggleColumn = (key) => setVisibleColumns((c) => (c.includes(key) ? (c.length === 1 ? c : c.filter((col) => col !== key)) : [...c, key]));
    const handleColumnDragStart = (e, key) => { setDraggedColumn(key); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', key); };
    const handleColumnDrop = (e, targetKey) => {
        e.preventDefault();
        const sourceKey = draggedColumn || e.dataTransfer.getData('text/plain');
        if (!sourceKey || sourceKey === targetKey) return;
        setVisibleColumns((current) => {
            const next = [...current];
            const srcIdx = next.indexOf(sourceKey); const tgtIdx = next.indexOf(targetKey);
            if (srcIdx === -1 || tgtIdx === -1) return current;
            next.splice(srcIdx, 1); next.splice(tgtIdx, 0, sourceKey);
            return next;
        });
        setDraggedColumn(null);
    };

    const resetFilters = () => {
        setQuery(''); setTypeFilter('all'); setSort({ key: 'date', direction: 'desc' });
        setVisibleColumns(DEFAULT_COLUMNS); setDraggedColumn(null); setPage(1);
    };

    const getAlignmentClass = (align) => {
        if (align === 'right') return styles.alignRight;
        if (align === 'center') return styles.alignCenter;
        return '';
    };

    const renderCell = (row, key) => {
        if (key === 'date') return <span className={styles.dateCell}>{row.date}</span>;
        if (key === 'invoice') return <span className={styles.invoiceLink}>{row.invoice}</span>;
        if (key === 'customer') return <div className={styles.customerCell}><strong className={styles.customerName}>{row.customer.name}</strong><span className={styles.customerPhone}>{row.customer.phone}</span></div>;
        if (key === 'type') return <span className={row.isB2B ? styles.badgeB2B : styles.badgeB2C}>{row.type}</span>;

        if (key === 'grossBilled') return <strong className={styles.moneyCell}>{formatMoney(row.grossBilled)}</strong>;
        if (key === 'prepaidAdjusted') {
            return (
                <span className={styles.taxCell} style={{ color: '#b45309', fontWeight: 'bold' }} title={row.packageAdjusted > 0 ? `Includes ${formatMoney(row.packageAdjusted)} of package services` : undefined}>
                    {formatMoney(row.prepaidAdjusted)}
                </span>
            );
        }
        if (key === 'packageAdjusted') return <span className={styles.taxCell} style={{ color: '#b45309' }}>{row.packageAdjusted > 0 ? formatMoney(row.packageAdjusted) : '—'}</span>;
        if (key === 'taxable') return <strong className={styles.moneyCell}>{formatMoney(row.taxableAmount)}</strong>;
        if (key === 'cgst' || key === 'sgst') return <span className={styles.taxCell}>{formatMoney(row[key])}</span>;
        if (key === 'totalGst') return <strong className={styles.totalTaxCell}>{formatMoney(row.totalGst)}</strong>;
        if (key === 'grandTotal') return <strong className={styles.moneyCell} style={{ color: '#148967' }}>{formatMoney(row.grandTotal)}</strong>;

        if (key === 'gstin') return <span className={styles.optionalText}>{row.gstin}</span>;
        if (key === 'placeOfSupply') return <span className={styles.optionalText}>{row.placeOfSupply || '—'}</span>;
        if (key === 'status') {
            const status = lower(row.status);
            const tone = status.includes('cancel') || status.includes('refund') ? 'danger' : status.includes('pending') || status.includes('due') ? 'warning' : 'success';
            return <span className={styles.statusBadge} data-tone={tone}>{row.status}</span>;
        }
        return '—';
    };

    if (isLoading) {
        return (
            <section className={styles.reportPanel}>
                <div className={styles.loadingState}>
                    <Loader2 size={25} className={styles.spin} />
                    <strong>Loading GST report</strong>
                    <span>Preparing your invoice sheet and tax totals…</span>
                </div>
            </section>
        );
    }

    return (
        <section className={styles.reportPanel}>
            {error ? (
                <div className={styles.errorState} role="alert">
                    <CircleAlert size={19} />
                    <div><strong>{error}</strong><span>Check the active store and try loading the report again.</span></div>
                    <button type="button" className={styles.retryButton} onClick={() => setRefreshKey((key) => key + 1)}>Retry</button>
                </div>
            ) : (
                <>
                    <div className={styles.sheetToolbar}>
                        <label className={styles.searchBox}>
                            <Search size={15} />
                            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search invoice, customer, GSTIN…" />
                        </label>
                        <select className={styles.tableSelect} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                            <option value="all">All types</option>
                            <option value="B2B">B2B only</option>
                            <option value="B2C">B2C only</option>
                        </select>
                        <div className={styles.toolbarSpacer} />

                        {canExport && hasAnyExportOption && (
                            <div className={styles.exportControl} ref={exportControlRef}>
                                <button type="button" className={styles.controlButton} onClick={() => setShowExport(!showExport)} disabled={isExporting}>
                                    <Download size={14} /> {isExporting ? 'Preparing…' : 'Download'} <ChevronDown size={12} />
                                </button>
                                {showExport && (
                                    <div className={styles.exportMenu} role="menu">
                                        {canExcel && <button type="button" className={styles.exportItem} onClick={handleExcelExport}><FileSpreadsheet size={14} /><span><strong>Excel workbook</strong><small>Current visible columns</small></span></button>}
                                        {canPdf && <button type="button" className={styles.exportItem} onClick={handlePDFExport}><FileText size={14} /><span><strong>PDF report</strong><small>Current visible columns</small></span></button>}
                                        {canCsv && <button type="button" className={styles.exportItem} onClick={handleCSVExport}><FileJson size={14} /><span><strong>CSV report</strong><small>Current visible columns</small></span></button>}
                                        {canPrint && <button type="button" className={styles.exportItem} onClick={() => { setShowExport(false); window.print(); }}><Printer size={14} /><span><strong>Print report</strong><small>Sheet-friendly layout</small></span></button>}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className={styles.columnControl} ref={columnControlRef}>
                            <button type="button" className={styles.controlButton} onClick={() => setShowColumns(!showColumns)}>
                                <SlidersHorizontal size={14} /> Columns <ChevronDown size={12} />
                            </button>
                            {showColumns && (
                                <div className={styles.columnMenu}>
                                    <strong>Visible columns</strong>
                                    {COLUMN_DEFINITIONS.map((column) => (
                                        <label key={column.key} className={styles.columnOption}>
                                            <input type="checkbox" checked={visibleColumns.includes(column.key)} onChange={() => toggleColumn(column.key)} />
                                            <span>{column.label}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button type="button" className={styles.resetButton} onClick={resetFilters}><RefreshCw size={14} /> Reset</button>
                    </div>

                    <div className={styles.tableWrapper}>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th className={styles.rowNumber}>#</th>
                                    {orderedColumnDefinitions.map((column) => {
                                        const isSorted = sort.key === column.key;
                                        const sortIcon = isSorted ? (sort.direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ChevronUp size={11} />;
                                        return (
                                            <th key={column.key} draggable="true"
                                                className={`${getAlignmentClass(column.align)} ${column.sortable ? styles.sortableHeader : ''}${draggedColumn === column.key ? ` ${styles.draggingHeader}` : ''}`}
                                                onClick={() => column.sortable && setSortKey(column.key)}
                                                onDragStart={(e) => handleColumnDragStart(e, column.key)}
                                                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                                                onDrop={(e) => handleColumnDrop(e, column.key)}
                                                onDragEnd={() => setDraggedColumn(null)}
                                            >
                                                <span>{column.label}</span>{column.sortable && sortIcon}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.length === 0 ? (
                                    <tr><td colSpan={visibleColumns.length + 1} className={styles.emptyTable}><Search size={20} /><strong>No matching GST invoices</strong><span>Try clearing the search.</span></td></tr>
                                ) : visibleRows.map((row, index) => (
                                    <tr key={row._key}>
                                        <td className={styles.rowNumber}>{(page - 1) * pageSize + index + 1}</td>
                                        {orderedColumnDefinitions.map((column) => (
                                            <td key={column.key} className={getAlignmentClass(column.align)}>{renderCell(row, column.key)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={styles.tablePager}>
                        <span>Showing <strong>{firstVisibleRow}–{lastVisibleRow}</strong> of <strong>{filteredRows.length}</strong></span>
                        <label>Rows
                            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className={styles.pageSizeSelect}>
                                <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                            </select>
                        </label>
                        <div className={styles.pageButtons}>
                            <button type="button" onClick={() => setPage((c) => Math.max(1, c - 1))} disabled={page <= 1}><ChevronLeft size={15} /></button>
                            <span>Page {page} / {totalPages}</span>
                            <button type="button" onClick={() => setPage((c) => Math.min(totalPages, c + 1))} disabled={page >= totalPages}><ChevronRight size={15} /></button>
                        </div>
                    </div>

                    <footer className={styles.fixedSummaryFooter}>
                        <div className={styles.summaryBlock}>
                            <span className={styles.blockTitle}>Billing Overview</span>
                            <div className={styles.metricRow}><span>Gross Billed</span><strong>{formatMoney(totals.totalGross)}</strong></div>
                            <div className={styles.metricRow}><span>Advance Adjusted</span><strong className={styles.orangeText}>{formatMoney(totals.totalPrepaid)}</strong></div>
                            {totals.totalPackage > 0 && (
                                <div className={styles.metricRow}><span>of which package services</span><strong className={styles.orangeText}>{formatMoney(totals.totalPackage)}</strong></div>
                            )}
                        </div>
                        <div className={styles.summaryBlock}>
                            <span className={styles.blockTitle}>Net GST Collection</span>
                            <div className={styles.metricRow}><span>Net Taxable</span><strong>{formatMoney(totals.totalTaxable)}</strong></div>
                            <div className={styles.metricRow}><span>Net CGST</span><strong>{formatMoney(totals.totalCgst)}</strong></div>
                            <div className={styles.metricRow}><span>Net SGST</span><strong>{formatMoney(totals.totalSgst)}</strong></div>
                        </div>
                        <div className={styles.grandTotalBox}>
                            <span>Total GST Payable</span>
                            <strong>{formatMoney(totals.totalGst)}</strong>
                        </div>
                        <div className={styles.grandTotalBox} style={{ paddingLeft: '20px', borderLeft: '1px solid #e2e8f0' }}>
                            <span>Net Collected</span>
                            <strong style={{ color: '#148967' }}>{formatMoney(totals.grandTotal)}</strong>
                        </div>
                    </footer>
                </>
            )}
        </section>
    );
}